export const KB_FALLBACK_MIN_QUERY = 4
export const KB_FALLBACK_SETTLE_MS = 600

/**
 * Minimal storage shape (matches `Storage`/`sessionStorage`) so tests can
 * inject a throwing stub without touching the DOM.
 */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function resolveStorage(storage?: StorageLike): StorageLike | null {
  if (storage) return storage
  if (typeof sessionStorage === "undefined") return null
  return sessionStorage
}

/**
 * `sessionStorage` throws `SecurityError` in Safari when cookies/storage are
 * blocked (private mode, strict tracking prevention, some enterprise MDM
 * profiles). These wrappers turn every access into a no-op / `null` on
 * throw instead of crashing the search UI. Pass `storage` to point at a
 * stub in tests.
 */
export function safeStorageGet(
  key: string,
  storage?: StorageLike
): string | null {
  try {
    return resolveStorage(storage)?.getItem(key) ?? null
  } catch {
    return null
  }
}

export function safeStorageSet(
  key: string,
  value: string,
  storage?: StorageLike
): void {
  try {
    resolveStorage(storage)?.setItem(key, value)
  } catch {
    // Storage unavailable — degrade to "nothing cached", not a crash.
  }
}

export function safeStorageRemove(key: string, storage?: StorageLike): void {
  try {
    resolveStorage(storage)?.removeItem(key)
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}

/**
 * Stage-0 gate: intent gating deliberately removed (decision 2026-08-07 with
 * Nick) — empirically the intent classifier nav-defaulted the dominant real
 * dead-end query shapes (bare marker/insurer names), so the "nav" exclusion
 * was silently swallowing exactly the queries this fallback exists to catch.
 * Stage 0 only ever renders KB links, which are harmless on a nav query.
 * Stage 1's generation trigger reintroduces intent gating.
 */
export function shouldFetchKbFallback({
  query,
  zeroResults,
}: {
  query: string
  zeroResults: boolean
}): boolean {
  return zeroResults && query.trim().length >= KB_FALLBACK_MIN_QUERY
}

export function kbFallbackCacheKey(
  country: string,
  locale: string,
  query: string
): string {
  return `ahead-kb-fallback:${country}:${locale}:${query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")}`
}

/**
 * Parses a cached KB fallback payload from sessionStorage. Returns `null` on
 * invalid JSON or a non-array payload so the caller can treat corrupt
 * storage as a cache miss instead of crashing.
 */
export function parseCachedKbResults<T>(raw: string): T[] | null {
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : null
  } catch {
    return null
  }
}
